import type {Client} from "typesense";
import type {CollectionCreateSchema} from "typesense/lib/Typesense/Collections";

import {ensureTypesenseSchema, type EnsureSchemaResult, type FieldDifference} from "./ensureSchema";

export interface SchemaGateCollectionResult {
    collection: string;
    result: EnsureSchemaResult;
}

export interface SchemaGateInconsistency {
    collection: string;
    differences: FieldDifference[];
}

export interface SchemaGateReport {
    /** Whether mutations were applied (prod) or only computed (dry-run/PR). */
    apply: boolean;
    results: SchemaGateCollectionResult[];
    /** Non-additive diffs across all collections; non-empty ⇒ gate fails. */
    inconsistencies: SchemaGateInconsistency[];
    ok: boolean;
}

export interface RunSchemaGateOptions {
    /** true ⇒ apply additive fields (prod); false ⇒ dry-run, mutate nothing (PR/preview). */
    apply: boolean;
    /**
     * Collection names opted in to auto-reconciliation of safe index-only
     * property drift (drop + re-add). Any collection not listed here keeps the
     * fail-closed behaviour: property drift is reported as an inconsistency and
     * never auto-applied. Defaults to none (opt-in per collection).
     */
    autoReconcileCollections?: ReadonlySet<string>;
    /**
     * Interval between live-schema polls while waiting for a collection
     * update that is still running on the server. See EnsureSchemaOptions.
     */
    updatePollIntervalMs?: number;
    /**
     * Maximum time to wait for a still-running collection update to finish.
     * See EnsureSchemaOptions.
     */
    updateWaitDeadlineMs?: number;
    logger?: {info: (msg: string) => void; warn: (msg: string) => void};
}

/**
 * Diffs every code-defined collection schema against its live counterpart.
 *
 * Additive optional fields are applied when `apply` is true (prod) and only
 * reported when false (dry-run). Collections listed in
 * `autoReconcileCollections` additionally have safe index-only property drift
 * auto-applied via drop + re-add. Any remaining non-additive diff is collected
 * as an inconsistency, which fails the gate (`ok === false`) in both modes. The
 * caller decides the process exit code; this function never exits.
 */
export async function runTypesenseSchemaGate(
    client: Client,
    schemas: readonly CollectionCreateSchema[],
    options: RunSchemaGateOptions,
): Promise<SchemaGateReport> {
    const logger = options.logger ?? console;
    const autoReconcileCollections = options.autoReconcileCollections ?? new Set<string>();
    const results: SchemaGateCollectionResult[] = [];
    const inconsistencies: SchemaGateInconsistency[] = [];

    for (const schema of schemas) {
        const result = await ensureTypesenseSchema(client, schema, {
            apply: options.apply,
            autoReconcile: autoReconcileCollections.has(schema.name),
            updatePollIntervalMs: options.updatePollIntervalMs,
            updateWaitDeadlineMs: options.updateWaitDeadlineMs,
            logger,
        });
        results.push({collection: schema.name, result});
        if (result.manualChangesNeeded.length > 0) {
            inconsistencies.push({collection: schema.name, differences: result.manualChangesNeeded});
        }
    }

    return {apply: options.apply, results, inconsistencies, ok: inconsistencies.length === 0};
}
